import { ArrowDownward } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Card, CardContent, Collapse, IconButton, Typography, useMediaQuery } from '@mui/material'
import { AxiosError } from 'axios'
import React, { useEffect, useState } from 'react'
import { useMutation, UseMutationResult } from 'react-query'
import {
  AdopterDto,
  AdoptionAttemptDto,
  AnimalDto,
  AnimalDtoSpecies,
  CustomerDto,
} from '../../../interactors/gen/backendClient'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { readableAxiosError } from '../../../utils/axios'
import { getAdoptionAttemptDocumentsSrc } from '../../../utils/S3-links'
import { colors, theme } from '../../theme'
import { addPrimaryFabSx, addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { getSoftChipSx } from '../../common/chipStyles'
import { AddAdoptionDocumentModal } from './AddAdoptionDocumentModal'
import { RefuseFileModal } from './RefuseFileModal'
import { SkipDocumentProcedureModal } from './SkipDocumentProcedureModal'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { ReviveAdopterPopUp } from './ReviveAdopterPopUp'

interface Props {
  title: string
  description: string
  setAdoptionAttempt?: React.Dispatch<React.SetStateAction<AdoptionAttemptDto | undefined>>
  adopter?: AdopterDto
  animal?: AnimalDto
  adoptionAttemptId?: string
  adoptionAttempt?: AdoptionAttemptDto
  type?: 'certificate' | 'contract' | 'sterilization'
  association?: CustomerDto | null
  originalFileKey?: string
  fileKey?: string
  validation?: boolean
  skipProcedure?: boolean
  expandable?: boolean
  defaultExpanded?: boolean
  specie?: AnimalDtoSpecies
  disabled?: boolean
  isReader?: boolean
  isLocked?: boolean
  updateAttemptMutation?: UseMutationResult<
    AdoptionAttemptDto,
    Error | AxiosError<unknown, unknown>,
    AdoptionAttemptDto,
    unknown
  >
  statusLabel?: string
  statusColor?: string
  manualImportMode?: boolean
  manualImportButtonLabel?: string
}

export const FileCard: React.FC<Props> = ({
  title,
  description,
  setAdoptionAttempt,
  adopter,
  animal,
  adoptionAttemptId,
  adoptionAttempt,
  type,
  fileKey,
  originalFileKey,
  association,
  validation = false,
  expandable,
  specie,
  skipProcedure = false,
  defaultExpanded = false,
  disabled = false,
  isReader = false,
  isLocked = false,
  updateAttemptMutation,
  statusLabel,
  statusColor,
  manualImportMode = false,
  manualImportButtonLabel,
}) => {
  const globalSnackBarStore = useGlobalSnackbarStore()

  const adopterId = adopter?.id

  const docAlreadyGiven = useMutation(
    async () => {
      if (!type) throw new Error('Type is required')
      if (type === 'sterilization') throw new Error('Sterilization cannot be delivered by hand')
      const formData = new FormData()
      formData.append('deliveredByHand', 'true')
      const response: AdoptionAttemptDto = await AdoptersClient.addOriginalDocumentToAdoptionAttempt(
        adopterId || 'unknown',
        adoptionAttemptId || 'unknown',
        formData,
        type
      )
      return response
    },
    {
      onSuccess: (data) => {
        if (setAdoptionAttempt) {
          setAdoptionAttempt(data)
          if (adopter)
            adopter.adoptionAttempts = adopter.adoptionAttempts?.map((a) => (a.id === adoptionAttemptId ? data : a))
        }
        globalSnackBarStore.triggerSuccessMessage(`Vos modifications ont bien été enregistrées.`)
      },
      onError: (error: Error | AxiosError) => {
        globalSnackBarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
      },
    }
  )

  useEffect(() => {
    if (defaultExpanded) setExpanded(true)
  }, [defaultExpanded])

  const [expanded, setExpanded] = useState(defaultExpanded || !expandable)

  // Toggle expand/collapse
  const handleToggleExpand = () => {
    if (expandable) {
      setExpanded(!expanded)
    }
  }

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('md'))
  const canRefuseDocument = !(adoptionAttempt?.done === true || adoptionAttempt?.archived === true)
  const shortTitle =
    title === "Certificat d'engagement" ? 'Certificat' : title === "Contrat d'adoption" ? 'Contrat' : title
  const initialDocumentHref =
    originalFileKey && originalFileKey !== 'NOT_PROVIDED.pdf'
      ? getAdoptionAttemptDocumentsSrc(originalFileKey)
      : undefined
  const alignedActionGroupSx = {
    width: { xs: '100%', md: 'auto' },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    flexWrap: 'nowrap',
    justifyContent: { xs: 'stretch', md: 'flex-end' },
    alignItems: { xs: 'stretch', md: 'center' },
    gap: 0.75,
    ml: { md: 'auto' },
  } as const

  const renderInitialDocumentLink = () => {
    if (!initialDocumentHref || isCompactScreen) {
      return null
    }

    return (
      <Box
        component="a"
        href={initialDocumentHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
        sx={{
          color: colors.cello,
          fontSize: 12.5,
          fontWeight: 700,
          lineHeight: '28px',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: colors.cello,
          },
        }}
      >
        Document initial
      </Box>
    )
  }

  const handleUnlockDocument = async () => {
    if (!adoptionAttempt || !type || !updateAttemptMutation) return

    const nextAttempt = {
      ...adoptionAttempt,
      [type]: { ...adoptionAttempt[type], isLocked: false },
    } as AdoptionAttemptDto

    setAdoptionAttempt?.(nextAttempt)

    try {
      await updateAttemptMutation.mutateAsync(nextAttempt)
    } catch (error) {
      setAdoptionAttempt?.(adoptionAttempt)
    }
  }

  return (
    <Card
      sx={{
        m: 0,
        height: '100%',
        border: '1px solid rgba(122, 129, 151, 0.16)',
        borderRadius: { xs: 0, md: 4 },
        boxShadow: { xs: 'none', md: '0 8px 22px rgba(26, 43, 77, 0.05)' },
      }}
    >
      <CardContent
        sx={{
          mt: 0,
          p: { xs: '18px !important', md: '9px 18px !important' },
          '&:last-child': { pb: { xs: '18px', md: '9px' } },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 0,
        }}
      >
        <Box
          onClick={handleToggleExpand}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1.5,
            cursor: expandable ? 'pointer' : 'default',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '16px', sm: '18px' }, color: '#1F2D4D' }}>
              {isCompactScreen ? shortTitle : title}
            </Typography>
            {statusLabel && (
              <Box
                component="span"
                sx={getSoftChipSx(statusColor, {
                  height: 28,
                  fontSize: 12,
                  labelWeight: 600,
                  emphasis: true,
                  maxWidth: 220,
                })}
              >
                <Typography
                  sx={{
                    px: 1.25,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: '28px',
                    whiteSpace: 'nowrap',
                    color: '#203864',
                  }}
                >
                  {statusLabel}
                </Typography>
              </Box>
            )}
            {renderInitialDocumentLink()}
          </Box>
          {expandable && (
            <IconButton>
              <ArrowDownward
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              />
            </IconButton>
          )}
        </Box>
        <Collapse in={expanded} timeout="auto">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 1.5, md: 2 },
              mt: { xs: 0, sm: 1 },
              pb: { xs: 'auto', sm: 1 },
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.cello,
                  fontSize: { xs: '13px', sm: '13.5px' },
                  fontStyle: 'italic',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {description}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 300 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', md: 'flex-end' },
                alignSelf: { xs: 'stretch', md: 'center' },
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 0.75,
              }}
            >
              {validation && adoptionAttempt && type && updateAttemptMutation ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    flexWrap: 'nowrap',
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 0.75,
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <Box sx={{ flex: { xs: 1, md: '0 1 auto' }, minWidth: { sm: 110 } }}>
                    <Button
                      component="a"
                      href={fileKey ? getAdoptionAttemptDocumentsSrc(fileKey) : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      fullWidth
                      sx={[
                        addSecondaryFabSx,
                        {
                          minHeight: 36,
                          height: 36,
                          px: { xs: 1.5, sm: 1.75 },
                          whiteSpace: 'nowrap',
                        },
                      ]}
                      disabled={!fileKey || disabled}
                    >
                      Consulter
                    </Button>
                  </Box>
                  {adoptionAttempt && type && setAdoptionAttempt && canRefuseDocument && (
                    <Box sx={{ flex: { xs: 1, md: '0 1 auto' }, minWidth: { sm: 118 } }}>
                      <RefuseFileModal
                        buttonText="Refuser"
                        fullWidth={isSmallScreen}
                        sx={{
                          minHeight: 36,
                          height: 36,
                          px: { xs: 1.5, sm: 1.75 },
                          whiteSpace: 'nowrap',
                        }}
                        type={type}
                        adopterId={adopterId || 'unknown'}
                        adoptionAttempt={adoptionAttempt}
                        setAdoptionAttempt={setAdoptionAttempt}
                        disabled={disabled}
                      />
                    </Box>
                  )}
                  <Box sx={{ flex: { xs: 1, md: '0 1 auto' }, minWidth: { sm: 154 } }}>
                    <Button
                      sx={{
                        ...addPrimaryFabSx,
                        fontSize: { xs: '14px', sm: '15px' },
                        minHeight: 36,
                        height: 36,
                        px: { xs: 1.75, sm: 2 },
                        whiteSpace: 'nowrap',
                      }}
                      variant="contained"
                      fullWidth
                      onClick={() =>
                        updateAttemptMutation.mutate({
                          ...adoptionAttempt,
                          [type]: { ...adoptionAttempt[type], validated: true },
                        })
                      }
                      disabled={disabled}
                    >
                      Valider la signature
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {originalFileKey &&
                    originalFileKey !== 'NOT_PROVIDED.pdf' &&
                    isLocked &&
                    updateAttemptMutation &&
                    adoptionAttempt &&
                    type && (
                      <Box sx={alignedActionGroupSx}>
                        <LoadingButton
                          variant="contained"
                          sx={[
                            addPrimaryFabSx,
                            {
                              fontSize: { xs: '14px', sm: '15px' },
                              minHeight: 36,
                              height: 36,
                              px: { xs: 1.75, sm: 2 },
                            },
                          ]}
                          fullWidth={isSmallScreen}
                          loading={updateAttemptMutation.isLoading}
                          onClick={() => handleUnlockDocument()}
                          disabled={disabled}
                        >
                          Envoyer
                        </LoadingButton>
                      </Box>
                    )}
                  {skipProcedure &&
                    type &&
                    adopter &&
                    adoptionAttemptId &&
                    setAdoptionAttempt &&
                    animal &&
                    association && (
                      <>
                        {!isLocked && (
                          <ReviveAdopterPopUp
                            animalId={animal.id}
                            adopterId={adopter.id}
                            animalName={animal.name}
                            customerName={association.name}
                          />
                        )}
                        <SkipDocumentProcedureModal
                          adopter={adopter}
                          adoptionAttemptId={adoptionAttemptId}
                          setAdoptionAttempt={setAdoptionAttempt}
                          type={type}
                          disabled={disabled}
                        />
                      </>
                    )}
                  {!validation &&
                    adopter &&
                    animal &&
                    adoptionAttemptId &&
                    type &&
                    setAdoptionAttempt &&
                    !isReader &&
                    !skipProcedure && (
                      <Box sx={alignedActionGroupSx}>
                        {type !== 'sterilization' && (
                          <AddAdoptionDocumentModal
                            setAdoptionAttempt={setAdoptionAttempt}
                            type={type}
                            adopter={adopter}
                            adoptionAttemptId={adoptionAttemptId}
                            specie={specie}
                            animal={animal}
                            association={association}
                            isLocked={isLocked}
                            buttonLabel={manualImportButtonLabel}
                            manualImportMode={manualImportMode}
                            disabled={disabled || docAlreadyGiven.isLoading || updateAttemptMutation?.isLoading}
                          />
                        )}
                        {!manualImportMode && (
                          <LoadingButton
                            variant="outlined"
                            fullWidth={isSmallScreen}
                            sx={[
                              addSecondaryFabSx,
                              {
                                fontSize: { xs: '14px', sm: '15px' },
                                minHeight: 38,
                                height: 38,
                              },
                            ]}
                            loading={docAlreadyGiven.isLoading || updateAttemptMutation?.isLoading}
                            disabled={disabled || docAlreadyGiven.isLoading || updateAttemptMutation?.isLoading}
                            onClick={() => {
                              docAlreadyGiven.mutate()
                            }}
                          >
                            Remis en main propre
                          </LoadingButton>
                        )}
                        {manualImportMode && fileKey && fileKey !== 'NOT_PROVIDED.pdf' && (
                          <Button
                            component="a"
                            href={getAdoptionAttemptDocumentsSrc(fileKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            fullWidth={isSmallScreen}
                            sx={[addSecondaryFabSx, { minHeight: 38, height: 38 }]}
                          >
                            {type === 'sterilization' ? 'Télécharger' : 'Consulter'}
                          </Button>
                        )}
                      </Box>
                    )}
                  {fileKey && fileKey !== 'NOT_PROVIDED.pdf' && !skipProcedure && !manualImportMode && (
                    <Button
                      component="a"
                      href={getAdoptionAttemptDocumentsSrc(fileKey)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      fullWidth={isSmallScreen}
                      sx={[addSecondaryFabSx, { minHeight: 38, height: 38 }]}
                    >
                      {type === 'sterilization' ? 'Télécharger' : 'Consulter'}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}
